import type {
  CreatedStaffMember,
  CreateStaffMember,
  IssuedInvite,
  StaffMember,
  UpdateStaffMember,
} from "@dentacore/shared-types";

import { api } from "./client";

/**
 * The clinic's staff. **Clinic-scoped, not branch-scoped** — one account spans
 * every branch, so listing them cannot depend on which one is active.
 */
export function listStaff(signal?: AbortSignal): Promise<StaffMember[]> {
  return api<StaffMember[]>("/users", { signal });
}

/**
 * Add someone. **Omit the password and they are invited** (STORY-064) — the
 * response carries a single-use link, shown once, which the admin hands over.
 */
export function createStaff(body: CreateStaffMember): Promise<CreatedStaffMember> {
  return api<CreatedStaffMember>("/users", { method: "POST", body });
}

/**
 * Change a role, or deactivate a leaver. **Admin only.**
 *
 * Refused by the server when it would leave the clinic with no active admin
 * (decision A3) — the message says so, and this call surfaces it unchanged.
 */
export function updateStaff(userId: string, body: UpdateStaffMember): Promise<StaffMember> {
  return api<StaffMember>(`/users/${userId}`, { method: "PATCH", body });
}

/** Give someone access to a branch. */
export function addMembership(userId: string, branchId: string): Promise<StaffMember> {
  return api<StaffMember>(`/users/${userId}/memberships`, { method: "POST", body: { branchId } });
}

/**
 * Take a branch away. **Immediate** — `BranchScopeGuard` reads memberships on
 * every request, so the next one they make is refused.
 */
export function removeMembership(userId: string, branchId: string): Promise<StaffMember> {
  return api<StaffMember>(`/users/${userId}/memberships/${branchId}`, { method: "DELETE" });
}

/** A fresh invite link, invalidating the one before it. Refused after acceptance. */
export function reissueInvite(userId: string): Promise<IssuedInvite> {
  return api<IssuedInvite>(`/users/${userId}/invite`, { method: "POST" });
}
